import { useCallback } from "react";

import { datadogRum } from "@datadog/browser-rum";
import { useNavigate } from "react-router";

import { InventoryChangeReason } from "__graphql__/types";
import { TGTGBagPreparationFeatures, TGTGBagUpdateStates } from "analytics/events";
import { routes } from "config/routes";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useCustomToast } from "shared/hooks/useCustomToast";

import { useUpdateSurpriseBagStockMutation } from "../queries/surpriseBagStock.generated";
import { ProductsMap, useTGTGBagStore } from "../stores/useTGTGBagStore";
import { transformToProductInput } from "../utils/helpers";

type CalculatePrices = {
  bagPrice: number;
  currentTotalPrice: number;
};

export function useSurpriseBagMutation() {
  const { sendSegmentTrackEvent } = useAnalytics();
  const { showToastUI } = useCustomToast();
  const { reset } = useTGTGBagStore();
  const navigate = useNavigate();
  const [updateSurpriseBagStockMutation, { loading }] = useUpdateSurpriseBagStockMutation();

  const updateSurpriseBagStock = useCallback(
    async ({
      bagId,
      bagSku,
      bagKey,
      productsMap,
      calculatePrices,
      maximumThreshold,
    }: {
      bagId: string | null;
      bagSku: string | null;
      bagKey: string | null;
      productsMap: ProductsMap;
      calculatePrices: CalculatePrices;
      maximumThreshold: number;
    }) => {
      if (loading || !bagId || !bagSku || !bagKey) return;
      const transformedProducts = transformToProductInput(productsMap);
      const reason = InventoryChangeReason.inbound_surprise_bag;
      try {
        await updateSurpriseBagStockMutation({
          variables: {
            input: {
              products: transformedProducts,
              reason,
              sku: bagSku,
              transactionId: `${bagId}-${bagSku}-${reason}`,
            },
          },
        });
        const { bagPrice, currentTotalPrice } = calculatePrices;
        sendSegmentTrackEvent("bagStateUpdated", {
          state: TGTGBagUpdateStates.BagPreparationFinished,
          bag_id: bagId!,
          bag_value_final: currentTotalPrice,
          bag_value_target: bagPrice,
          bag_type: bagKey,
          bag_value_limit: bagPrice + maximumThreshold,
          bag_preparation_feature: TGTGBagPreparationFeatures.TgtgAssistant,
        });
        reset();
        navigate(routes.inventory.tooGoodToGo.confirmation);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Unknown error";
        datadogRum.addError("Error updateSurpriseBagStockMutation", {
          errorMessage,
          variables: {
            products: transformedProducts,
            reason,
            sku: bagSku,
            transactionId: `${bagId}-${bagSku}-${reason}`,
          },
        });
        showToastUI({
          id: "inbound-rescue-bag-error-toast",
          title: "inbound-rescue-bag-error-toast",
        });
      }
    },
    [loading, navigate, reset, sendSegmentTrackEvent, showToastUI, updateSurpriseBagStockMutation],
  );
  return { updateSurpriseBagStock, loading };
}
